import React from "react";
import { Container, Section, Eyebrow, Reveal, CountUp, Hi, Icon, Button } from "../components/primitives.jsx";
import { SectionHead, IconTile, CTABand, ProductCard, ArticleCard } from "../components/blocks.jsx";
import { AIConsole, PartnerLogo } from "../components/visuals.jsx";
import { ArticleReader } from "../components/ArticleReader.jsx";

export function Home({ go, c, radius = 16 }) {
  const t = c.home;
  const washTone = "wash";
  const [openArticle, setOpenArticle] = React.useState(null);

  return (
    <main style={{ fontFamily: "var(--font-sans)" }}>
      {/* HERO */}
      <section style={{ background: "#fefcff", position: "relative", overflow: "hidden" }}>
        <div aria-hidden="true" className="rw-glow-a" style={{ position: "absolute", inset: "-35%", zIndex: 0, pointerEvents: "none", willChange: "transform", backgroundImage: "radial-gradient(circle at 35% 65%, rgba(173, 216, 230, 0.5), transparent 55%)" }} />
        <div aria-hidden="true" className="rw-glow-b" style={{ position: "absolute", inset: "-35%", zIndex: 0, pointerEvents: "none", willChange: "transform", backgroundImage: "radial-gradient(circle at 65% 35%, rgba(255, 182, 193, 0.55), transparent 55%)" }} />
        <div aria-hidden="true" style={{ position: "absolute", right: "-2%", top: "8%", fontFamily: "var(--font-display)", fontWeight: 800, fontSize: "clamp(120px, 22vw, 360px)", letterSpacing: "-0.05em", color: "rgba(30,30,30,0.04)", lineHeight: 1, userSelect: "none", pointerEvents: "none", zIndex: 0 }}>3R</div>
        <Container style={{ position: "relative", zIndex: 1 }}>
          <div className="rw-hero-grid" style={{ display: "grid", gridTemplateColumns: "1.05fr 0.95fr", gap: "var(--space-9)", alignItems: "center", padding: "clamp(3rem, 7vw, var(--space-12)) 0" }}>
            <div>
              <Reveal>
                <div style={{ display: "inline-flex", alignItems: "center", gap: 9, padding: "7px 14px 7px 8px", borderRadius: "var(--radius-pill)", background: "var(--neutral-50)", border: "1px solid var(--border-subtle)" }}>
                  <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 22, height: 22, borderRadius: 999, background: "var(--brand-red)" }}>
                    <Icon name="refresh-cw" size={13} color="#fff" strokeWidth={2.4} />
                  </span>
                  <span style={{ fontSize: "var(--text-xs)", fontWeight: 700, letterSpacing: "0.04em", color: "var(--text-body)" }}>{t.eyebrow}</span>
                </div>
              </Reveal>
              <Reveal delay={70}>
                <h1 style={{ margin: "22px 0 0", fontFamily: "var(--font-display)", fontWeight: 800, fontSize: "clamp(2.6rem, 6vw, var(--text-5xl))", lineHeight: 1.02, letterSpacing: "var(--tracking-tighter)", color: "var(--brand-ink)", textWrap: "balance" }}>
                  {t.titleA}<br /><Hi>{t.titleB}</Hi>
                </h1>
              </Reveal>
              <Reveal delay={140}>
                <p style={{ margin: "22px 0 0", maxWidth: 480, fontSize: "var(--text-md)", lineHeight: 1.65, color: "var(--text-body)", textWrap: "pretty" }}>{t.lede}</p>
              </Reveal>
              <Reveal delay={210}>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-3)", marginTop: "var(--space-7)" }}>
                  <Button variant="primary" size="lg" onClick={() => go("contact")} iconRight={<Icon name="arrow-right" size={18} color="#fff" />}>{t.ctaPrimary}</Button>
                  <Button variant="secondary" size="lg" onClick={() => go("products")}>{t.ctaSecondary}</Button>
                </div>
              </Reveal>
              <Reveal delay={280}>
                <p style={{ margin: "var(--space-5) 0 0", display: "inline-flex", alignItems: "center", gap: 8, fontSize: "var(--text-sm)", color: "var(--text-muted)", fontWeight: 500, textWrap: "pretty" }}>
                  <Icon name="shield-check" size={16} color="var(--brand-red)" /> {t.heroProof}
                </p>
              </Reveal>
            </div>
            <Reveal delay={160} y={26}>
              <AIConsole radius={radius} />
            </Reveal>
          </div>
        </Container>
      </section>

      {/* PARTNER BAR */}
      <section style={{ background: "var(--surface-wash)", borderTop: "1px solid var(--border-subtle)", borderBottom: "1px solid var(--border-subtle)" }}>
        <Container>
          <div className="rw-partnerbar" style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "center", gap: "clamp(1.5rem, 5vw, var(--space-10))", padding: "var(--space-6) 0" }}>
            <span style={{ fontSize: "var(--text-xs)", fontWeight: 700, letterSpacing: "var(--tracking-widest)", textTransform: "uppercase", color: "var(--text-muted)", whiteSpace: "nowrap" }}>{t.partnersLabel}</span>
            <div className="rw-partnerbar-logos" style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "center", gap: "clamp(1.5rem, 4vw, var(--space-9))" }}>
              {t.partners.map((p) => (
                <span key={p.name} title={p.kind} style={{ display: "inline-flex", alignItems: "center" }}>
                  <PartnerLogo name={p.name} />
                </span>
              ))}
            </div>
          </div>
        </Container>
      </section>

      {/* STAT BAND */}
      <section style={{ background: "var(--brand-ink)", position: "relative", overflow: "hidden" }}>
        <div aria-hidden="true" style={{ position: "absolute", right: "-4%", top: "-30%", width: 360, height: 360, borderRadius: 999, border: "1px solid rgba(255,255,255,0.06)" }} />
        <Container style={{ position: "relative" }}>
          <div style={{ padding: "var(--space-10) 0" }}>
            <Reveal>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "flex-end", justifyContent: "space-between", marginBottom: "var(--space-8)" }}>
                <div>
                  <Eyebrow onDark>{t.statsEyebrow}</Eyebrow>
                  <h2 style={{ margin: "12px 0 0", fontFamily: "var(--font-display)", fontWeight: 800, fontSize: "clamp(1.8rem, 3.4vw, var(--text-3xl))", lineHeight: 1.08, letterSpacing: "var(--tracking-tight)", color: "#fff" }}>{t.statsTitle}</h2>
                </div>
              </div>
            </Reveal>
            <div className="rw-stat-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "var(--space-5)" }}>
              {t.stats.map((s, i) => (
                <Reveal key={i} delay={i * 60}>
                  <div
                    style={{ height: "100%", boxSizing: "border-box", padding: "var(--space-6)", borderRadius: radius, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", display: "flex", flexDirection: "column", gap: 12, transition: "background var(--duration-base) var(--ease-out)" }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.07)")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.04)")}
                  >
                    <span style={{ width: 44, height: 44, borderRadius: Math.max(8, radius - 6), background: "var(--brand-red)", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxShadow: "var(--shadow-brand)" }}>
                      <Icon name={s.icon} size={22} color="#fff" strokeWidth={2} />
                    </span>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
                      <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: "clamp(2.2rem, 4.4vw, var(--text-4xl))", letterSpacing: "-0.03em", color: "#fff", lineHeight: 1 }}>
                        <CountUp value={s.value} />
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: "var(--text-md)", fontWeight: 700, color: "#fff", letterSpacing: "-0.01em", lineHeight: 1.25 }}>{s.label}</div>
                      <div style={{ marginTop: 4, fontSize: "var(--text-sm)", color: "var(--neutral-400)", lineHeight: 1.4 }}>{s.sub}</div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </Container>
      </section>

      {/* SERVICES */}
      <Section tone={washTone}>
        <Reveal><SectionHead eyebrow={t.servicesEyebrow} title={t.servicesTitle} lede={t.servicesLede} maxLede={620} /></Reveal>
        <div className="rw-svc-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "var(--space-5)", marginTop: "var(--space-8)" }}>
          {c.services.map((s, i) => (
            <Reveal key={i} delay={i * 80}>
              <div
                onClick={() => go("services")}
                role="button"
                tabIndex={0}
                aria-label={s.title}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); go("services"); } }}
                style={{ background: "var(--surface-card)", borderRadius: radius, padding: "var(--space-5)", height: "100%", border: "1px solid var(--border-subtle)", boxShadow: "var(--shadow-sm)", cursor: "pointer", display: "flex", flexDirection: "column", gap: 14, transition: "transform var(--duration-base) var(--ease-out), box-shadow var(--duration-base) var(--ease-out)" }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = "var(--shadow-md)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "var(--shadow-sm)"; }}
              >
                <IconTile name={s.icon} tone={i % 2 === 0 ? "red" : "ink"} radius={Math.max(8, radius - 4)} />
                <h3 style={{ margin: 0, fontSize: "var(--text-md)", fontWeight: 700, letterSpacing: "-0.01em", color: "var(--brand-ink)", lineHeight: 1.25 }}>{s.title}</h3>
                <p style={{ margin: 0, fontSize: "var(--text-sm)", lineHeight: 1.6, color: "var(--text-muted)" }}>{s.body}</p>
                {s.gain && (
                  <div style={{ marginTop: "auto", paddingTop: 6, display: "inline-flex", alignItems: "center", gap: 7 }}>
                    <Icon name="arrow-up-right" size={15} color="var(--brand-red)" strokeWidth={2.4} />
                    <span style={{ fontSize: "var(--text-xs)", fontWeight: 700, color: "var(--brand-red)" }}>{s.gain}</span>
                  </div>
                )}
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* PRODUCTS SHOWCASE */}
      <Section tone="page">
        <div className="rw-split" style={{ display: "grid", gridTemplateColumns: "0.9fr 1.1fr", gap: "var(--space-9)", alignItems: "center" }}>
          <Reveal>
            <SectionHead eyebrow={t.productsEyebrow} title={t.productsTitle} lede={t.productsLede} />
            <div style={{ marginTop: "var(--space-6)" }}>
              <Button variant="dark" size="md" onClick={() => go("products")} iconRight={<Icon name="arrow-right" size={16} color="#fff" />}>{t.productsCta}</Button>
            </div>
          </Reveal>
          <div className="rw-prod2" style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "var(--space-4)" }}>
            {c.products.slice(0, 4).map((p, i) => (
              <Reveal key={i} delay={i * 70}><ProductCard p={p} radius={radius} cta={c.productsPage.learnMore} onClick={() => go("products")} /></Reveal>
            ))}
          </div>
        </div>
      </Section>

      {/* TRUST */}
      <Section tone="page">
        <Reveal>
          <div style={{ textAlign: "center" }}>
            <Eyebrow>{t.trustEyebrow}</Eyebrow>
            <p style={{ margin: "12px auto 0", maxWidth: 620, fontSize: "var(--text-md)", color: "var(--text-body)", lineHeight: 1.6 }}>{t.trustLabel}</p>
          </div>
        </Reveal>
        <Reveal delay={80}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "center", marginTop: "var(--space-6)" }}>
            {t.trustSectors.map((s) => (
              <span key={s} style={{ fontFamily: "var(--font-mono)", fontSize: "var(--text-xs)", fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--text-muted)", background: "var(--neutral-50)", border: "1px solid var(--border-subtle)", padding: "8px 16px", borderRadius: "var(--radius-pill)" }}>{s}</span>
            ))}
          </div>
        </Reveal>
        <Reveal delay={120}>
          <div className="rw-case" style={{ marginTop: "var(--space-8)", background: "var(--brand-ink)", borderRadius: radius + 6, padding: "clamp(2rem, 4vw, var(--space-9))", display: "grid", gridTemplateColumns: "auto 1fr", gap: "var(--space-8)", alignItems: "center" }}>
            <div className="rw-case-stat" style={{ borderRight: "1px solid rgba(255,255,255,0.12)", paddingRight: "var(--space-8)" }}>
              <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: "clamp(3rem, 7vw, var(--text-5xl))", color: "var(--brand-red)", lineHeight: 1, letterSpacing: "-0.03em" }}>{t.caseStat}</div>
              <div style={{ marginTop: 8, fontSize: "var(--text-sm)", color: "var(--neutral-400)", fontWeight: 600 }}>{t.caseStatLabel}</div>
            </div>
            <div>
              <Icon name="quote" size={26} color="var(--brand-red)" />
              <p style={{ margin: "12px 0 0", fontSize: "clamp(1.1rem, 2vw, var(--text-lg))", lineHeight: 1.5, color: "#fff", fontWeight: 600, textWrap: "pretty" }}>{t.caseQuote}</p>
              <div style={{ marginTop: "var(--space-4)", fontSize: "var(--text-sm)", color: "var(--neutral-400)" }}>{t.caseAttr}</div>
            </div>
          </div>
        </Reveal>
      </Section>

      {/* WHY / VALUES */}
      <Section tone="ink">
        <Reveal><SectionHead eyebrow={t.whyEyebrow} title={t.whyTitle} lede={t.whyLede} onDark maxLede={640} /></Reveal>
        <div className="rw-svc-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "var(--space-5)", marginTop: "var(--space-8)" }}>
          {t.values.map((v, i) => (
            <Reveal key={i} delay={i * 80} style={{ padding: "var(--space-5)", borderRadius: radius, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", height: "100%", boxSizing: "border-box" }}>
              <IconTile name={v.icon} tone="red" radius={Math.max(8, radius - 4)} />
              <h3 style={{ margin: "16px 0 0", fontSize: "var(--text-md)", fontWeight: 700, color: "#fff", letterSpacing: "-0.01em" }}>{v.title}</h3>
              <p style={{ margin: "8px 0 0", fontSize: "var(--text-sm)", lineHeight: 1.6, color: "var(--neutral-400)" }}>{v.body}</p>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* ARTICLES TEASER */}
      <Section tone={washTone}>
        <Reveal>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "flex-end", justifyContent: "space-between" }}>
            <SectionHead eyebrow={t.articlesEyebrow} title={t.articlesTitle} />
            <Button variant="ghost" size="md" onClick={() => go("articles")} iconRight={<Icon name="arrow-right" size={16} color="var(--brand-red)" />}>{t.articlesCta}</Button>
          </div>
        </Reveal>
        <div className="rw-svc-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "var(--space-5)", marginTop: "var(--space-7)" }}>
          {c.articlesPage.items.slice(0, 3).map((a, i) => (
            <Reveal key={a.slug || i} delay={i * 80}><ArticleCard a={a} radius={radius} label={c.articlesPage.readMore} onClick={() => (a.slug ? go(a.slug) : setOpenArticle(a))} /></Reveal>
          ))}
        </div>
      </Section>

      <CTABand title={t.ctaTitle} lede={t.ctaLede} button={t.ctaButton} onClick={() => go("contact")} radius={radius} />

      <ArticleReader article={openArticle} labels={{ close: c.articlesPage.close, readerNote: c.articlesPage.readerNote, talk: c.articlesPage.talk }} onClose={() => setOpenArticle(null)} go={go} />
    </main>
  );
}
